/**
 * Thin browser logging. Console always; Faro remote when REACT_APP_FARO_URL is set.
 */
import { faro } from '@grafana/faro-web-sdk';
import { getSessionCorrelationId } from './correlation';

const SENSITIVE = /password|token|authorization|secret|cookie|signature/i;

function redact(value, depth = 0) {
  if (depth > 4) return '[Truncated]';
  if (value == null) return value;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map((v) => redact(v, depth + 1));
  if (typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((k) => {
      out[k] = SENSITIVE.test(k) ? '[REDACTED]' : redact(value[k], depth + 1);
    });
    return out;
  }
  return value;
}

function pushToFaro(level, message, fields) {
  // ponytail: faro may be uninitialized when REACT_APP_FARO_URL unset
  try {
    if (!faro?.api) return;
    if (level === 'error' || level === 'fatal' || level === 'warn') {
      faro.api.pushLog([message], { level: level === 'warn' ? 'warn' : 'error', context: fields || {} });
    }
  } catch (_) {
    // ignore
  }
}

export function createClientLogger({ service, version }) {
  const base = {
    service,
    version: version || 'unknown',
    environment: process.env.NODE_ENV || 'development',
    component: 'frontend',
    correlationId: getSessionCorrelationId(),
  };

  function write(level, event, message, fields = {}) {
    if (level === 'debug' && process.env.NODE_ENV === 'production') return;

    const payload = redact({
      timestamp: new Date().toISOString(),
      level,
      event,
      message,
      ...base,
      ...fields,
    });
    const line = JSON.stringify(payload);
    if (level === 'error' || level === 'fatal') {
      // eslint-disable-next-line no-console
      console.error(line);
      pushToFaro(level, message, {
        event,
        correlationId: payload.correlationId,
        requestId: payload.requestId,
        ...fields,
      });
    } else if (level === 'warn') {
      // eslint-disable-next-line no-console
      console.warn(line);
      pushToFaro(level, message, {
        event,
        correlationId: payload.correlationId,
        requestId: payload.requestId,
        ...fields,
      });
    } else {
      // eslint-disable-next-line no-console
      console.info(line);
    }
  }

  function init() {
    write('info', 'app.started', `${service} started`, {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      correlationId: getSessionCorrelationId(),
    });

    if (typeof window === 'undefined') return;

    window.addEventListener('error', (e) => {
      write('error', 'frontend.error', e.message || 'Uncaught error', {
        error: {
          name: e.error && e.error.name,
          message: e.message,
          stack: e.error && e.error.stack,
        },
        source: e.filename,
        line: e.lineno,
      });
    });

    window.addEventListener('unhandledrejection', (e) => {
      const reason = e.reason;
      write('error', 'frontend.unhandled_rejection', (reason && reason.message) || String(reason), {
        error: {
          name: reason && reason.name,
          message: reason && reason.message ? reason.message : String(reason),
          stack: reason && reason.stack,
        },
      });
    });
  }

  function apiFailed(message, fields) {
    write('error', 'http.outbound.failed', message, fields);
  }

  function debug(event, message, fields = {}) {
    write('debug', event, message, fields);
  }

  function info(event, message, fields = {}) {
    write('info', event, message, fields);
  }

  function warn(event, message, fields = {}) {
    write('warn', event, message, fields);
  }

  function error(event, message, fields = {}) {
    write('error', event, message, fields);
  }

  return { init, write, apiFailed, debug, info, warn, error };
}
